import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroSection from './components/HeroSection';
import CategorySection from './components/CategorySection';
import PopularSection from './components/PopularSection';
import LatestSection from './components/LatestSection';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '喵趣漫画网',
  url: 'https://miaoqucomic.com',
  description: '喵趣漫画网为您汇集韩漫、日漫与国漫免费全集，支持下拉式在线阅读，让您一口气看完热门漫画大全。',
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'contact@miaoqucomic.com',
    contactType: 'customer service',
    availableLanguage: ['Chinese'],
  },
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '喵趣漫画网',
  url: 'https://miaoqucomic.com',
  description: '韩漫日漫国漫免费全集在线阅读平台',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: 'https://miaoqucomic.com/search?q={search_term_string}',
    },
    'query-input': 'required name=search_term_string',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '喵趣漫画网提供哪些类型的漫画？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '喵趣漫画网汇集韩漫、日漫、国漫等多种类型的漫画资源，包括玄幻、都市、搞笑、悬疑、恋爱、动作、奇幻等热门分类，全部免费在线阅读。',
      },
    },
    {
      '@type': 'Question',
      name: '喵趣漫画网的阅读方式是什么？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '喵趣漫画网支持下拉式在线阅读，无需翻页，流畅滚动即可一口气看完热门漫画大全，为您提供最舒适的漫画阅读体验。',
      },
    },
    {
      '@type': 'Question',
      name: '喵趣漫画网是否需要注册？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '喵趣漫画网所有漫画资源完全免费，无需注册登录即可直接在线阅读韩漫、日漫、国漫全集，方便快捷。',
      },
    },
    {
      '@type': 'Question',
      name: '喵趣漫画网的更新频率如何？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '喵趣漫画网每日更新热门漫画，韩漫、日漫、国漫连载作品持续跟进，确保您第一时间看到最新话内容。',
      },
    },
    {
      '@type': 'Question',
      name: '如何在喵趣漫画网找到想看的漫画？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '您可以通过首页的分类导航按韩漫、日漫、国漫等类别筛选，也可以使用搜索功能查找特定漫画。热门推荐板块也会为您精选高分漫画作品。',
      },
    },
  ],
};

export default function Home() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <HeroSection />
          <CategorySection />
          <PopularSection />
          <LatestSection />

          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="text-center">
              <h2 className="text-xl md:text-3xl font-heading text-foreground-950 mb-3">
                为什么选择喵趣漫画网？
              </h2>
              <p className="text-sm text-foreground-500 mb-10">我们致力于打造最好的免费漫画在线阅读平台</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: 'ri-book-read-line', title: '海量资源', desc: '收录韩漫、日漫、国漫等1000+部热门漫画，类型丰富多样，满足你的所有阅读需求。' },
                { icon: 'ri-smartphone-line', title: '下拉式阅读', desc: '支持流畅的下拉式在线阅读体验，无需点击翻页，滑动即可连续阅读，一口气看到底。' },
                { icon: 'ri-money-cny-circle-line', title: '完全免费', desc: '无需注册登录，所有漫画资源完全免费，不设VIP限制，真正的全民免费阅读平台。' },
                { icon: 'ri-timer-flash-line', title: '每日更新', desc: '热门连载漫画每日同步更新，韩漫、日漫、国漫最新话第一时间呈现，追漫不等待。' },
              ].map((item, idx) => (
                <div key={idx} className="p-5 bg-background-50 rounded-lg border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                    <i className={`${item.icon} text-xl text-primary-500`}></i>
                  </div>
                  <h3 className="font-medium text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-3xl font-heading text-foreground-950">
                读者热评
              </h2>
              <p className="mt-2 text-sm text-foreground-500">来自喵趣漫画网读者的真实反馈</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                { name: '小猫咪', text: '喵趣漫画网的下拉式阅读真的太方便了！韩漫资源也很全，每天上班摸鱼必备。', rating: 5 },
                { name: '漫画迷小王', text: '国漫资源更新特别快，一人之下每周都第一时间更新，画质清晰流畅，强烈推荐！', rating: 5 },
                { name: '日漫控', text: '日漫专区分类很细致，找到好多之前没看过的宝藏漫画，还全是免费的，太良心了。', rating: 4 },
                { name: '追更达人', text: '每天打开喵趣漫画网追更新已经成了习惯，韩漫日漫国漫都有，一个网站全搞定。', rating: 5 },
                { name: '书荒拯救者', text: '书荒的时候就来喵趣漫画网的热门推荐区逛逛，每次都能发现新的宝藏漫画，好评！', rating: 5 },
                { name: '深夜读者', text: '下拉式阅读模式对手机用户太友好了，躺着看漫画超舒服，已经推荐给朋友们了。', rating: 4 },
              ].map((review, idx) => (
                <div key={idx} className="p-5 bg-background-100 rounded-lg border border-background-200/70">
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: 5 }, (_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-3">{review.text}</p>
                  <p className="text-xs text-foreground-400">— {review.name}</p>
                </div>
              ))}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}